import { toast } from 'sonner'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useContext } from 'react'

import { AnalyticsEvent, RoutesPath } from 'src/types'
import { StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageUserCars } from 'src/hooks/useManageUserCars'
import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { transformDataCarDates } from 'src/helpers/transformDataCarDates'
import { getFormattedDate } from 'src/helpers/getFormattedDate'
import { AnalyticsContext } from 'src/contexts/AnalyticsContext'

import { FormData } from 'src/pages/ServiceMaintenance/types'
import { getForm, getSelectFields } from 'src/pages/ServiceMaintenance/helpers'
import { DEFAULT_VALUES } from 'src/pages/ServiceMaintenance/constants'
import { getActiveCar } from 'src/pages/FuelTracking/helpers'
import { queryClient } from 'src/features/AppProviders/constants'
import { MaintenanceProps } from 'src/api/Maintenance/types'

export const useMaintenance = () => {
  const { translations } = useLocales()
  const { navigateTo } = useNavigation()
  const { logEvent } = useContext(AnalyticsContext)

  const { currency, distanceMetric, activeCar } = useAppSelector(
    state => state.user,
  )

  const { addMaintenanceHistory, isLoadingAddMaintenanceHistory } =
    useManageMaintenanceHistory()
  const { userCars, updateCar, isLoadingGeneral } = useManageUserCars()

  const {
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors },
    getValues,
  } = useForm<FormData>({
    defaultValues: DEFAULT_VALUES,
  })

  const updateUserCar = (mileage: number, lastServiceDate: Date): void => {
    const userActiveCar = getActiveCar(userCars)

    if (!userActiveCar) return

    const car = transformDataCarDates(userActiveCar)
    updateCar(
      {
        carNumber: activeCar,
        newData: { ...car, mileage, lastServiceDate },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [StorePersistKeys.USER_CARS],
          })
        },
      },
    )
  }

  const onSubmit: SubmitHandler<FormData> = (data: FormData): void => {
    const { date, serviceCost, odometer } = getValues()

    const newItemHistory: MaintenanceProps = {
      ...data,
      details: data.details.trim(),
      address: data.address.trim(),
      odometer: Number(odometer) || 0,
      serviceCost: Number(serviceCost) || 0,
      date: getFormattedDate(date),
      currency,
      distanceUnit: distanceMetric,
    }

    addMaintenanceHistory(newItemHistory, {
      onSuccess: () => {
        reset(DEFAULT_VALUES)
        logEvent(AnalyticsEvent.ADD_NEW_MAINTENANCE)
        toast(translations.maintenance.addMaintenance.toaster, {
          description: translations.maintenance.addMaintenance.toasterBody,
          action: {
            label: 'Okay',
            onClick: () => toast.dismiss(),
          },
        })

        queryClient.invalidateQueries({
          queryKey: [StorePersistKeys.MAINTENANCE_HISTORY],
        })

        updateUserCar(newItemHistory.odometer, newItemHistory.date)
      },
    })
  }

  const getFormattedTitle = (name: string, title: string): string => {
    const isCurrency = name === 'serviceCost'
    const isOdometer = name === 'odometer'

    if (isCurrency) return `${title} (${currency.toUpperCase()})`

    if (isOdometer) return `${title} (${distanceMetric})`

    return title
  }

  const gotoMyCars = (): void => {
    navigateTo(RoutesPath.MY_CARS)
  }

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.serviceMaintenance,
      href: '/service',
    },
    { labelKey: translations.breadcrumbItems.add },
  ]

  return {
    locale: translations.maintenance.addMaintenance,
    breadcrumbItems,
    fields: getForm(translations.maintenance),
    selectFields: getSelectFields(translations.maintenance),
    getFormattedTitle,
    handleSubmit,
    onSubmit,
    control,
    setValue,
    errors,
    isLoadingAddMaintenanceHistory:
      isLoadingAddMaintenanceHistory || isLoadingGeneral,
    activeCar,
    gotoMyCars,
  }
}
